import type { CSSVariablesResolver } from "@mantine/core";

/**
 * Named surface, border, and text tokens (docs/UI_STANDARDS.md, "Lucid-Inspired Surface, Density, and Color
 * Direction"). This module and `palette.ts` are the only places in the repository where product colors are written as
 * hex; everything else consumes the CSS variables these emit, Mantine color names, or semantic roles.
 *
 * The ladder is page → sunken → panel → raised → floating → overlay. Dark mode is a near-black dashboard ground with
 * charcoal layers, not Mantine's default navy-gray; pure black is reserved for deliberate full-bleed media.
 */
export interface SurfaceTokens {
  /** Application background. Not a card. */
  readonly page: string;
  /** Secondary wells: chart and table recesses, control tracks. */
  readonly sunken: string;
  /** Standard cards, tables, filter regions, sidebars. */
  readonly panel: string;
  /** Controls inside panels: inputs, selects, segmented controls, compact toolbars. */
  readonly raised: string;
  /** Menus, popovers, dropdowns, tooltips. */
  readonly floating: string;
  /** Modal and drawer surfaces over the approved scrim. */
  readonly overlay: string;
  /**
   * A navigation row under the pointer, and the resting fill of the selected row. Its own token because the two
   * schemes need different steps: on dark it lands on `raised`, but on light `raised` is the same white as the page,
   * so a hover there has to come from the sunken side instead.
   */
  readonly navHover: string;
  /** The selected navigation row under the pointer: one step further than `navHover`. */
  readonly navActiveHover: string;
  readonly borderSubtle: string;
  readonly borderDefault: string;
  readonly borderStrong: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly textDisabled: string;
}

export const LIGHT_SURFACES: SurfaceTokens = {
  page: "#FFFFFF",
  sunken: "#F6F7F8",
  panel: "#FFFFFF",
  raised: "#FFFFFF",
  floating: "#FFFFFF",
  overlay: "#FFFFFF",
  navHover: "#F6F7F8",
  navActiveHover: "#EFF1F3",
  borderSubtle: "#E5E7EB",
  borderDefault: "#D1D5DB",
  borderStrong: "#B8C0CA",
  textPrimary: "#14171A",
  textSecondary: "#5F6670",
  /**
   * Deviation from the supplied palette, measured not guessed: the specified #7C848E reaches only 3.79:1 on the
   * white page and less on the sunken surface where table headers sit, below the 4.5:1 this codebase needs because
   * muted text is used at body size. This is the same hue darkened 14%, at 4.87:1 on page and 4.54:1 on sunken.
   */
  textMuted: "#6B727A",
  /** Disabled text is exempt from WCAG contrast (SC 1.4.3) and stays at the specified value. */
  textDisabled: "#A7ADB5",
};

export const DARK_SURFACES: SurfaceTokens = {
  page: "#090A0C",
  sunken: "#0C0E11",
  panel: "#111317",
  raised: "#171A1F",
  floating: "#1A1E24",
  overlay: "#1C2027",
  navHover: "#171A1F",
  navActiveHover: "#1A1E24",
  borderSubtle: "#252A31",
  borderDefault: "#303640",
  borderStrong: "#414955",
  textPrimary: "#F5F7FA",
  textSecondary: "#B5BDC8",
  textMuted: "#7F8996",
  textDisabled: "#59616D",
};

/** Pure black is never a default surface; it is available only for deliberate immersive/full-bleed media. */
export const IMMERSIVE_BLACK = "#000000";

const VARIABLE_NAMES: Readonly<Record<keyof SurfaceTokens, string>> = {
  page: "--how-surface-page",
  sunken: "--how-surface-sunken",
  panel: "--how-surface-panel",
  raised: "--how-surface-raised",
  floating: "--how-surface-floating",
  overlay: "--how-surface-overlay",
  navHover: "--how-nav-hover",
  navActiveHover: "--how-nav-active-hover",
  borderSubtle: "--how-border-subtle",
  borderDefault: "--how-border-default",
  borderStrong: "--how-border-strong",
  textPrimary: "--how-text-primary",
  textSecondary: "--how-text-secondary",
  textMuted: "--how-text-muted",
  textDisabled: "--how-text-disabled",
};

function toVariables(tokens: SurfaceTokens): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, name] of Object.entries(VARIABLE_NAMES)) {
    out[name] = tokens[key as keyof SurfaceTokens];
  }
  return out;
}

/** CSS variable name for a surface token, for the rare component that needs it in TypeScript. */
export function surfaceVar(token: keyof SurfaceTokens): string {
  return `var(${VARIABLE_NAMES[token]})`;
}

/**
 * Emits the surface tokens per color scheme. Mantine scopes the light/dark blocks itself, so a component can read
 * `var(--how-surface-panel)` without a scheme conditional. Passed to MantineProvider by `AppProviders`.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    /** Elevation for floating layers only; panels are defined by borders, not shadows. */
    "--how-shadow-floating":
      "0 8px 24px -8px rgba(3, 5, 8, 0.28), 0 2px 6px -2px rgba(3, 5, 8, 0.2)",
    "--how-shadow-panel": "none",
    "--how-motion-fast": "140ms",
    "--how-motion-overlay": "190ms",
    "--how-motion-ease": "cubic-bezier(0.2, 0.8, 0.3, 1)",
  },
  light: toVariables(LIGHT_SURFACES),
  dark: toVariables(DARK_SURFACES),
});
